/**
 * Member-side Sports Club for Health seed (player Health motivation).
 * Club policy fields (sporting aims, apply-as, current offer) stay on the club seed.
 * Must stay in sync with Backend/src/utils/healthPlayerProfiling.js
 */
import { PURPOSE_HEALTH } from "./profilingFocusCatalog";
import {
  resolveWhoBandFromAge,
  resolveWhoBandFromBirthDate,
  ageYearsFromBirthDate,
} from "./whoPhysicalActivityByAge";

export { PURPOSE_HEALTH };

export const EMPTY_HEALTH_PLAYER_SEED = {
  healthDimensions: [],
  whoSelfCheck: "",
  whoBand: "",
  primaryFocusId: null,
  secondaryFocusId: null,
  delivery: "",
  intensity: "",
  sessionsPerWeek: "",
  experienceLevelId: null,
  coachNote: "",
};

export const HEALTH_DIMENSIONS = ["physical", "mental", "social"];

export const HEALTH_WHO_SELF_CHECK = ["below", "around", "above"];

export const HEALTH_DELIVERY = ["into_coaching", "separate_sessions", "both"];

export const HEALTH_INTENSITY = ["regular", "gentle", "adapted"];

export const HEALTH_SESSIONS = ["1", "2", "3+"];

/** Focus IDs from tbl_attributes, grouped by SCforH dimension. */
export const HEALTH_FOCUS_BY_DIMENSION = {
  physical: [5, 2, 21, 1],
  mental: [6, 19],
  social: [4],
};

export const HEALTH_ADAPTED_FOCUS_IDS = [20, 22, 21];

export const DEFAULT_HEALTH_PHASE_WEEKS = 16;

export const DEFAULT_HEALTH_SESSION_HOURS = 1.5;

export function isHealthPurpose(purposeId) {
  return Number(purposeId) === PURPOSE_HEALTH;
}

function pickAllowed(list, allowed) {
  if (!Array.isArray(list)) return [];
  const set = allowed instanceof Set ? allowed : new Set(allowed);
  return [...new Set(list.map((v) => String(v || "").trim()).filter((v) => set.has(v)))];
}

function oneOf(value, allowed) {
  const v = String(value || "").trim();
  const set = allowed instanceof Set ? allowed : new Set(allowed);
  return set.has(v) ? v : "";
}

export function healthFocusIdsForDimensions(dimensions, { intensity } = {}) {
  const dims = pickAllowed(dimensions, HEALTH_DIMENSIONS);
  const ids = new Set();
  const use = dims.length ? dims : HEALTH_DIMENSIONS;
  for (const d of use) {
    for (const id of HEALTH_FOCUS_BY_DIMENSION[d] || []) ids.add(id);
  }
  if (intensity === "gentle" || intensity === "adapted") {
    for (const id of HEALTH_ADAPTED_FOCUS_IDS) ids.add(id);
  }
  return [...ids];
}

export function normalizeHealthPlayerSeed(raw, extras = {}) {
  const src = raw && typeof raw === "object" ? raw : {};
  const sessions = oneOf(src.sessionsPerWeek, HEALTH_SESSIONS);
  let whoBand = oneOf(src.whoBand, ["children", "adolescents", "adults", "seniors"]);
  if (!whoBand && extras.dateOfBirth) {
    whoBand = resolveWhoBandFromBirthDate(extras.dateOfBirth);
  }
  if (!whoBand && extras.ageYears != null) {
    whoBand = resolveWhoBandFromAge(extras.ageYears);
  }
  const primary = src.primaryFocusId != null ? Number(src.primaryFocusId) : null;
  const secondary = src.secondaryFocusId != null ? Number(src.secondaryFocusId) : null;
  return {
    healthDimensions: pickAllowed(src.healthDimensions, HEALTH_DIMENSIONS),
    whoSelfCheck: oneOf(src.whoSelfCheck, HEALTH_WHO_SELF_CHECK),
    whoBand: whoBand || "adults",
    primaryFocusId: Number.isFinite(primary) && primary > 0 ? primary : null,
    secondaryFocusId: Number.isFinite(secondary) && secondary > 0 ? secondary : null,
    delivery: oneOf(src.delivery, HEALTH_DELIVERY),
    intensity: oneOf(src.intensity, HEALTH_INTENSITY),
    sessionsPerWeek: sessions,
    experienceLevelId:
      src.experienceLevelId != null && Number(src.experienceLevelId) > 0
        ? Number(src.experienceLevelId)
        : null,
    coachNote: String(src.coachNote || "").trim().slice(0, 500),
  };
}

export function isHealthPlayerSeedComplete(seed) {
  const s = normalizeHealthPlayerSeed(seed);
  return Boolean(
    s.healthDimensions.length &&
      s.whoSelfCheck &&
      s.primaryFocusId &&
      s.delivery &&
      s.intensity &&
      s.sessionsPerWeek &&
      s.experienceLevelId
  );
}

export function seedFromHealthSlots(slots) {
  const h = slots?.health || {};
  return normalizeHealthPlayerSeed(
    {
      healthDimensions: h.healthDimensions,
      whoSelfCheck: h.whoSelfCheck,
      whoBand: h.whoBand,
      primaryFocusId: slots?.S?.primaryFocusId ?? h.primaryFocusId,
      secondaryFocusId: (slots?.S?.secondaryFocusIds || [])[0] ?? h.secondaryFocusId,
      delivery: h.delivery,
      intensity: h.intensity,
      sessionsPerWeek: slots?.M?.weeklyTraining?.sessionsPerWeek ?? h.sessionsPerWeek,
      experienceLevelId: slots?.M?.experienceLevelId ?? h.experienceLevelId,
      coachNote: h.coachNote,
    },
    { dateOfBirth: slots?.R?.dateOfBirth }
  );
}

export function formatHealthPlayerSeedForPrompt(seed) {
  const s = normalizeHealthPlayerSeed(seed);
  if (!s.healthDimensions.length && !s.whoSelfCheck) return "";
  const lines = [
    "PLAYER HEALTH SEED (Sports Club for Health, member side — not a medical record):",
  ];
  if (s.healthDimensions.length) {
    lines.push(`- Dimensions to grow: ${s.healthDimensions.join(", ")}`);
  }
  if (s.whoBand) lines.push(`- WHO age band: ${s.whoBand}`);
  if (s.whoSelfCheck) lines.push(`- Self-check vs WHO guideline: ${s.whoSelfCheck}`);
  if (s.primaryFocusId) lines.push(`- Primary health focus id: ${s.primaryFocusId}`);
  if (s.secondaryFocusId) lines.push(`- Secondary health focus id: ${s.secondaryFocusId}`);
  if (s.delivery) lines.push(`- Preferred delivery: ${s.delivery}`);
  if (s.intensity) lines.push(`- Intensity / adaptation: ${s.intensity}`);
  if (s.sessionsPerWeek) lines.push(`- Club sessions per week: ${s.sessionsPerWeek}`);
  if (s.experienceLevelId) lines.push(`- Table tennis experience level id: ${s.experienceLevelId}`);
  if (s.coachNote) lines.push(`- Optional coach note (no diagnoses): ${s.coachNote}`);
  lines.push(
    "- Club table tennis minutes count toward WHO totals; they are not TT-only quotas. Never use the quota as a guilt metric. Do not mention WHO/OMS in chat except once, at the end of the training cycle."
  );
  return lines.join("\n");
}

export function whoBandFromUserDob(dateOfBirth) {
  return resolveWhoBandFromBirthDate(dateOfBirth);
}

export function ageYearsFromUserDob(dateOfBirth) {
  return ageYearsFromBirthDate(dateOfBirth);
}
